/**
 * ExtractStatusBadge Component
 *
 * Renders an extraction status value as a coloured badge.
 * The background colour varies depending on the status value to provide
 * at-a-glance visual feedback in table views.
 */

import { Badge } from "@/components/ui/badge"
import { extractionStatusLabelMap } from "@/services/journalArticlesService"

/** Props for the ExtractStatusBadge component. */
interface ExtractStatusBadgeProps {
  /** The raw extraction status value from the article data. */
  status: string
}

/**
 * Map extraction status values to Tailwind colour classes.
 * Colours are chosen to convey meaning:
 * - Not Started → neutral grey
 * - In Progress → blue (active work)
 * - Extracted → green (complete)
 * - Editing → amber (in review)
 * - Excluded → red (removed)
 * - Extracted via Comms → teal (alternate completion path)
 */
const STATUS_COLOUR_MAP: Record<string, string> = {
  "NOT_STARTED": "bg-gray-100 text-gray-700 border-gray-300",
  "IN_PROGRESS": "bg-blue-100 text-blue-700 border-blue-300",
  "EXTRACTED": "bg-green-100 text-green-700 border-green-300",
  "EDITING": "bg-amber-100 text-amber-700 border-amber-300",
  "EXCLUDED": "bg-red-100 text-red-700 border-red-300",
  "EXTRACTED_VIA_COMMS": "bg-teal-100 text-teal-700 border-teal-300",
}

/** Default colour when the status value is not recognised. */
const DEFAULT_COLOUR = "bg-gray-100 text-gray-700 border-gray-300"

/**
 * Renders an extraction status as a coloured badge.
 *
 * @param props - Component props.
 * @param props.status - The raw extraction status string.
 * @returns A Badge element with status-dependent colouring.
 */
export default function ExtractStatusBadge({ status }: ExtractStatusBadgeProps): React.JSX.Element {
  const colourClass = STATUS_COLOUR_MAP[status] ?? DEFAULT_COLOUR
  const label = extractionStatusLabelMap[status] ?? status

  return (
    <Badge variant="outline" className={colourClass}>
      {label}
    </Badge>
  )
}
